/**
 * @file SettingsSubgroup.tsx
 * @description Уровни 2 и служебная подсказка внутри группы настроек
 * (phase19.16, SB122; uiux-settings-groups §3 — не больше трёх уровней:
 * группа → подгруппа → подпись поля).
 *
 *  - `SettingsSubgroup` — уровень 2: заголовок `type-body` 600 тёмный, без
 *    линии и карточки, отступ сверху 20 («Вид полосы», «Ссылки»);
 *  - `FieldHint` — подсказка под полем: `type-caption` 400 серый, отступ 4.
 * Уровень 3 — подпись поля `Label` из `ui/label.tsx`.
 * `status` подгруппы (SB120 этап 3а) — значок «✕» / «!» у заголовка (тот же
 * смысл, что у группы: «нужно исправить» / «есть предупреждение»).
 *
 * ```tsx
 * <SettingsSubgroup title="Ссылки">…<FieldHint>Оферта на сайте всегда.</FieldHint></SettingsSubgroup>
 * ```
 */

import { CircleAlert, CircleX } from "lucide-react";
import { useId, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface SubgroupProps {
  title: string;
  children: ReactNode;
  className?: string;
  /** Значок у заголовка: ✕ — нужно исправить, ! — предупреждение. */
  status?: "error" | "warning";
}

const SUBGROUP_STATUS = {
  error: { icon: CircleX, className: "text-destructive", label: "нужно исправить" },
  warning: { icon: CircleAlert, className: "text-warning", label: "есть предупреждение" },
} as const;

export function SettingsSubgroup({ title, children, className, status }: SubgroupProps) {
  const titleId = useId();
  const meta = status ? SUBGROUP_STATUS[status] : null;
  return (
    <div role="group" aria-labelledby={titleId} data-slot="settings-subgroup" data-status={status} className={cn("flex min-w-0 flex-col gap-3 pt-2", className)}>
      <h4 id={titleId} className="type-body m-0 flex items-center gap-1.5 font-semibold text-on-surface">
        {title}
        {meta ? (
          <>
            <meta.icon aria-hidden className={cn("size-4 shrink-0", meta.className)} />
            <span className="sr-only">— {meta.label}</span>
          </>
        ) : null}
      </h4>
      {children}
    </div>
  );
}

interface HintProps {
  children: ReactNode;
  id?: string;
  className?: string;
}

export function FieldHint({ children, id, className }: HintProps) {
  return (
    <p id={id} data-slot="field-hint" className={cn("type-caption mt-1 break-words font-normal text-on-surface-variant", className)}>
      {children}
    </p>
  );
}
